import { useCallback } from 'react';
import { View } from 'react-native';
import { DockedPanel } from '../layout/DockedPanel';
import { PanelStack, usePanelStack } from '../layout/PanelStack';
import { PLAN_PANEL_WIDTH } from '../../theme/layout';
import { useUIStore } from '../../store/uiStore';
import { useEscapeToClose } from '../../utils/escapeStack';
import { MapLayersContent } from './MapLayersContent';
import { PlanMapImportForm } from './PlanMapImportForm.web';

function MapLayersPanelRoot({ onClose }: { onClose: () => void }) {
  const { push, pop } = usePanelStack();

  // Import a plan map (panel-system.md Phase 2): a PanelStack push inside
  // this same left dock rather than the global `planMapImport` sheet — see
  // MapLayersContent's `onImportMap` prop. `pop()` both backs out of an
  // in-progress import and finishes a completed one (PlanMapImportForm's
  // `onClose` covers both).
  const handleImportMap = useCallback(() => {
    push({
      key: 'plan-map-import',
      title: 'Import a plan map',
      render: () => <PlanMapImportForm visible onClose={pop} />,
    });
  }, [push, pop]);

  return (
    <View testID="map-layers-sheet">
      <MapLayersContent onClose={onClose} showCloseButton onImportMap={handleImportMap} />
    </View>
  );
}

/**
 * Desktop-shell presentation of the map layers content: a left-docked,
 * non-modal panel instead of the phone's centered-dialog sheet (Glenn, Sept
 * 23 2026 call: the sheet ran too tall and covered the map). Mounted only
 * while uiStore.layersPanelOpen is true — same "mount is open" contract as
 * every other DockedPanel usage. Wraps its content in a PanelStack so
 * importing a plan map (panel-system.md Phase 2) drills in rather than
 * opening a second dock.
 *
 * Mounted in app/(tabs)/index.tsx AFTER the three plan-editor panels (same
 * left dock, same z-index): mount order decides which one paints on top
 * when both are open, and Layers should win over the plan editor it was
 * opened on top of.
 */
export function MapLayersPanel({
  shown = true,
  onExited,
}: {
  /** False while the panel slides out (see useLeavingPanel). */
  shown?: boolean;
  onExited?: () => void;
}) {
  const closeLayersPanel = useUIStore((s) => s.closeLayersPanel);

  useEscapeToClose(shown, closeLayersPanel);

  return (
    <DockedPanel
      side="left"
      width={PLAN_PANEL_WIDTH}
      testID="map-layers-panel"
      shown={shown}
      onExited={onExited}>
      <PanelStack root={<MapLayersPanelRoot onClose={closeLayersPanel} />} />
    </DockedPanel>
  );
}
